import { useEffect, useState } from 'react';
import AgentService from '../services/agent.service';
import { useAppDispatch, useAppSelector } from '../hooks/redux-hooks';
import { setChatData } from '../store/chat.slice';
import { setThreadId } from '../store/common.slice';
import useStorage from '../hooks/useStorage';

interface UserChat {
  _id: string;
  chatId: string;
  chat: any[];
  createdAt: string;
  assistantId?: string;
  agentId?: string;
  threadId?: string;
}

const parseDate = (dateStr: string): Date => new Date(dateStr);

const getDaysDiff = (date1: Date, date2: Date): number => {
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

const groupItemsByDate = (items: UserChat[]): Record<string, UserChat[]> => {
  const today = new Date();
  const groups: Record<string, UserChat[]> = {
    Today: [],
    Yesterday: [],
    'Previous 7 Days': [],
    Older: [],
  };

  items.forEach((item) => {
    const itemDate = parseDate(item.createdAt);
    const daysDiff = getDaysDiff(itemDate, today);
    if (daysDiff === 0) {
      groups['Today'].push(item);
    } else if (daysDiff === 1) {
      groups['Yesterday'].push(item);
    } else if (daysDiff <= 7) {
      groups['Previous 7 Days'].push(item);
    } else {
      groups['Older'].push(item);
    }
  });
  return groups;
};

const Navbar = () => {
  const [chats, setChats] = useState<UserChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { setItem } = useStorage();
  const { userInfo } = useAppSelector((state) => state.common);

  useEffect(() => {
    if (!userInfo?._id) return;
    const fetchChats = async () => {
      try {
        setLoading(true);
        const response = await AgentService.getChatsByUserId(
          userInfo?._id || ''
        );
        if (response.data?.data && response.data?.data?.userChats) {
          setChats(response.data?.data?.userChats || []);
        } else {
          setError(response.data?.message || 'Failed to fetch chats');
        }
      } catch (err: any) {
        console.error('Error fetching chats:', err);
        setError('Failed to fetch chats');
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [userInfo?._id]);

  const handleChatClick = (chat: UserChat) => {
    try {
      // Format chat data for display (convert AgentQA to chat message format)
      const chatMessages = chat.chat.map((item) => ({
        ques: item.question,
        answer: item.answer,
        msgId: item.msgId,
        model: item.model,
        assistantId: item.assistantId,
        agentId: item.agentId,
        replyText: item.replyText,
        chatId: chat.chatId,
        threadId: chat.threadId,
      }));

      // Set chat data to Redux
      dispatch(setChatData(chatMessages));

      // Set threadId to Redux
      if (chat.threadId) {
        dispatch(setThreadId(chat.threadId));
      }

      // Set chat data to sessionStorage
      setItem('chatData', JSON.stringify(chatMessages));

      // Set threadId to sessionStorage
      if (chat.threadId) {
        setItem('threadId', chat.threadId);
      }

      // Also set chatId
      setItem('chatId', chat.chatId);

      console.log('Chat loaded:', {
        chatId: chat.chatId,
        threadId: chat.threadId,
      });
    } catch (err) {
      console.error('Error loading chat:', err);
    }
  };

  const userChatsByDate = groupItemsByDate(chats);

  return (
    <div className="w-[250px] h-full bg-white shadow-md fixed top-0 left-0 flex flex-col z-10 pt-[65px]">
      <div className="text-[14px] text-[#8f8f8f] dark:text-[#bdbdbd] px-3 mb-4">
        Chat Library
      </div>

      {loading && (
        <div className="text-[12px] text-[#8f8f8f] px-3">Loading chats...</div>
      )}

      {error && <div className="text-[12px] text-red-500 px-3">{error}</div>}

      {!loading && !error && chats.length === 0 && (
        <div className="text-[12px] text-[#8f8f8f] px-3">No chats found</div>
      )}

      {!loading && !error && chats.length > 0 && (
        <div className="w-full px-3 grow overflow-y-auto">
          {Object.keys(userChatsByDate).map((group) => {
            if (userChatsByDate[group] && userChatsByDate[group].length > 0) {
              return (
                <div className="w-full" key={group}>
                  <div className="text-xs text-[#7c7c7c] dark:text-[#AFAFAF]">
                    {group}
                  </div>
                  <ul className="border-l-2 border-[#e0e0e0] dark:border-[#444] pl-1 mt-4 mb-4 ml-1.5">
                    {userChatsByDate[group].map((chat) => {
                      const lastQuestion =
                        chat.chat && chat.chat.length > 0
                          ? chat.chat[chat.chat.length - 1]?.question
                          : 'Untitled Chat';

                      return (
                        <li
                          key={chat._id}
                          onClick={() => handleChatClick(chat)}
                          className="
                            h-[30px] group relative min-w-0 pl-2 flex items-center text-xs rounded-lg
                            text-[#0d0d0d] dark:text-white transition hover:bg-hoverBgColor
                            dark:hover:bg-[#424242] cursor-pointer font-light
                          "
                        >
                          <p className="overflow-hidden text-ellipsis whitespace-nowrap relative grow text-[14px]">
                            {lastQuestion}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

export default Navbar;
